import React, { useEffect, useState } from 'react';
import client from '../../api/client';
import Navbar from '../../components/Navbar';
import { useAuth } from '../../context/AuthContext';
import ReviewCanvas from '../../components/reviewer/ReviewCanvas';
import BoxSidebar from '../../components/reviewer/BoxSidebar';

export default function ReviewQueue() {
  const { user } = useAuth();
  const [annotation, setAnnotation] = useState(null);
  const [skippedIds, setSkippedIds] = useState([]);
  const [loading, setLoading] = useState(true);
  const [empty, setEmpty] = useState(false);
  const [error, setError] = useState('');
  const [acting, setActing] = useState(false);

  // Reject modal state
  const [showModal, setShowModal] = useState(false);
  const [rejectReason, setRejectReason] = useState('');
  const [rejectError, setRejectError] = useState('');

  const fetchNext = async (exclude) => {
    setLoading(true);
    setError('');
    setAnnotation(null);
    try {
      const q = exclude.length > 0 ? `?exclude=${exclude.join(',')}` : '';
      const res = await client.get(`/api/review/next/${q}`);
      setAnnotation(res.data);
      setEmpty(false);
    } catch (e) {
      if (e.response?.status === 404) {
        setEmpty(true);
      } else {
        setError('Failed to load next annotation.');
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchNext([]); }, []);

  const handleApprove = async () => {
    setActing(true);
    setError('');
    try {
      await client.post(`/api/review/${annotation.annotation_id}/approve/`);
      // reset skip list — new item may have been behind skipped ones
      const nextSkipped = skippedIds;
      await fetchNext(nextSkipped);
    } catch {
      setError('Failed to approve annotation.');
    } finally {
      setActing(false);
    }
  };

  const handleRejectSubmit = async () => {
    const trimmed = rejectReason.trim();
    if (!trimmed) {
      setRejectError('Please enter a rejection reason.');
      return;
    }
    setActing(true);
    setRejectError('');
    try {
      await client.post(`/api/review/${annotation.annotation_id}/reject/`, { reason: trimmed });
      setShowModal(false);
      setRejectReason('');
      await fetchNext(skippedIds);
    } catch {
      setRejectError('Failed to reject annotation.');
    } finally {
      setActing(false);
    }
  };

  const handleSkip = async () => {
    const newSkipped = [...skippedIds, annotation.annotation_id];
    setSkippedIds(newSkipped);
    await fetchNext(newSkipped);
  };

  const openRejectModal = () => {
    setRejectReason('');
    setRejectError('');
    setShowModal(true);
  };

  return (
    <div style={styles.page}>
      <Navbar />

      {/* Reject modal */}
      {showModal && (
        <div style={styles.modalOverlay}>
          <div style={styles.modal}>
            <h3 style={styles.modalTitle}>Reject Annotation</h3>
            <p style={styles.modalSub}>Provide a reason — the annotator will see this and re-label the image.</p>
            <textarea
              autoFocus
              value={rejectReason}
              onChange={e => setRejectReason(e.target.value)}
              rows={4}
              placeholder="e.g. Boxes are too loose, label 'cat' is wrong…"
              style={styles.textarea}
            />
            {rejectError && <p style={styles.modalError}>{rejectError}</p>}
            <div style={styles.modalBtns}>
              <button
                style={{ ...styles.modalBtn, background: '#34495e' }}
                onClick={() => setShowModal(false)}
                disabled={acting}
              >
                Cancel
              </button>
              <button
                style={{ ...styles.modalBtn, background: '#E74C3C' }}
                onClick={handleRejectSubmit}
                disabled={acting}
              >
                {acting ? 'Rejecting…' : 'Reject'}
              </button>
            </div>
          </div>
        </div>
      )}

      <div style={styles.workspace}>
        {/* Left: canvas */}
        <div style={styles.canvasCol}>
          {loading && <p style={styles.loadingMsg}>Loading annotation…</p>}
          {error && <p style={styles.errorMsg}>{error}</p>}
          {empty && (
            <div style={styles.emptyState}>
              <div style={styles.emptyIcon}>✓</div>
              <h2 style={styles.emptyTitle}>Queue is empty</h2>
              <p style={styles.emptySub}>
                {skippedIds.length > 0
                  ? `You've skipped ${skippedIds.length} annotation${skippedIds.length !== 1 ? 's' : ''}. They'll reappear when you revisit.`
                  : 'No submitted annotations assigned to you right now.'}
              </p>
              {skippedIds.length > 0 && (
                <button
                  style={styles.clearSkipBtn}
                  onClick={() => { setSkippedIds([]); fetchNext([]); }}
                >
                  Review skipped annotations
                </button>
              )}
            </div>
          )}
          {!loading && !error && !empty && annotation && (
            <ReviewCanvas imageUrl={annotation.signed_image_url} boxes={annotation.boxes} />
          )}
        </div>

        {/* Right: sidebar */}
        <div style={styles.sidebar}>
          <div style={styles.sideHeader}>
            <span style={styles.sideTitle}>Review Queue</span>
            <span style={styles.reviewer}>— {user?.name}</span>
          </div>

          {annotation && (
            <BoxSidebar
              boxes={annotation.boxes}
              annotatorName={annotation.annotator_name}
            />
          )}
        </div>
      </div>

      {/* Bottom action bar */}
      {!loading && !empty && annotation && (
        <div style={styles.bottomBar}>
          <button
            style={{ ...styles.barBtn, ...styles.skipBtn }}
            onClick={handleSkip}
            disabled={acting}
          >
            Skip
          </button>
          <button
            style={{ ...styles.barBtn, ...styles.rejectBtn }}
            onClick={openRejectModal}
            disabled={acting}
          >
            Reject
          </button>
          <button
            style={{ ...styles.barBtn, ...styles.approveBtn }}
            onClick={handleApprove}
            disabled={acting}
          >
            {acting ? 'Saving…' : 'Approve'}
          </button>
        </div>
      )}
    </div>
  );
}

const styles = {
  page: { minHeight: '100vh', background: '#1a1a2e', fontFamily: 'sans-serif', display: 'flex', flexDirection: 'column' },

  modalOverlay: {
    position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.7)',
    display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 500,
  },
  modal: {
    background: '#1e293b', borderRadius: '10px', padding: '1.5rem',
    width: '420px', maxWidth: '90vw', border: '1px solid #334155',
  },
  modalTitle: { margin: '0 0 0.25rem', color: '#e2e8f0', fontSize: '1.1rem' },
  modalSub: { margin: '0 0 1rem', color: '#94a3b8', fontSize: '0.85rem' },
  textarea: {
    width: '100%', boxSizing: 'border-box', padding: '0.6rem',
    background: '#0f172a', border: '1px solid #475569', borderRadius: '6px',
    color: '#e2e8f0', fontSize: '0.9rem', resize: 'vertical', fontFamily: 'sans-serif',
  },
  modalError: { color: '#E74C3C', fontSize: '0.82rem', margin: '0.5rem 0 0' },
  modalBtns: { display: 'flex', gap: '0.75rem', marginTop: '1rem', justifyContent: 'flex-end' },
  modalBtn: { padding: '0.55rem 1.2rem', border: 'none', borderRadius: '6px', color: '#fff', fontWeight: 600, cursor: 'pointer', fontSize: '0.9rem' },

  workspace: { display: 'flex', flex: 1, overflow: 'hidden' },
  canvasCol: { flex: 1, overflow: 'auto', padding: '1rem', minWidth: 0 },
  sidebar: {
    width: '260px', minWidth: '260px', background: '#16213e',
    padding: '1rem', overflowY: 'auto', borderLeft: '1px solid #0f3460',
    display: 'flex', flexDirection: 'column', gap: '1rem',
  },
  sideHeader: { display: 'flex', flexDirection: 'column', gap: '2px' },
  sideTitle: { color: '#e0e0e0', fontSize: '1rem', fontWeight: 600 },
  reviewer: { color: '#64748b', fontSize: '0.8rem' },

  loadingMsg: { color: '#94a3b8', padding: '2rem 0' },
  errorMsg: { color: '#E74C3C', padding: '2rem 0' },

  emptyState: {
    display: 'flex', flexDirection: 'column', alignItems: 'center',
    justifyContent: 'center', height: '60vh', gap: '1rem', textAlign: 'center',
  },
  emptyIcon: { fontSize: '3rem', color: '#2ECC71' },
  emptyTitle: { margin: 0, color: '#e0e0e0', fontSize: '1.4rem' },
  emptySub: { margin: 0, color: '#94a3b8', fontSize: '0.9rem', maxWidth: '320px' },
  clearSkipBtn: {
    padding: '0.5rem 1.2rem', background: '#3498DB', color: '#fff',
    border: 'none', borderRadius: '6px', cursor: 'pointer', fontSize: '0.9rem',
  },

  bottomBar: {
    background: '#16213e', borderTop: '1px solid #0f3460',
    padding: '0.75rem 1.5rem', display: 'flex', gap: '0.75rem', justifyContent: 'flex-end',
  },
  barBtn: { padding: '0.55rem 1.5rem', border: 'none', borderRadius: '6px', fontSize: '0.95rem', fontWeight: 600, cursor: 'pointer' },
  skipBtn: { background: '#34495e', color: '#fff' },
  rejectBtn: { background: '#E74C3C', color: '#fff' },
  approveBtn: { background: '#2ECC71', color: '#fff' },
};
